<script setup lang="ts">
import type { FormInst, FormRules } from 'naive-ui';

import { ref, watch } from 'vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';

interface Props {
  initialName?: string;
  isEdit?: boolean;
}

interface Emits {
  (e: 'submit', data: { name: string; token: string }): void;
}

const props = withDefaults(defineProps<Props>(), {
  initialName: '',
  isEdit: false,
});

const emit = defineEmits<Emits>();

const formRef = ref<FormInst | null>(null);

const formData = ref({
  name: props.initialName,
  token: '',
});

const rules: FormRules = {
  name: [
    {
      required: true,
      message: 'Please input a token name',
      trigger: ['blur', 'input'],
    },
  ],
  token: [
    {
      required: true,
      message: 'Please input a token value',
      trigger: ['blur', 'input'],
    },
  ],
};

watch(
  () => props.initialName,
  newName => {
    formData.value.name = newName;
  }
);

function handleSubmit(): void {
  void formRef.value?.validate(errors => {
    ((): void => {
      if (!errors) {
        emit('submit', {
          name: formData.value.name,
          token: formData.value.token,
        });
      }
    })();
  });
}

function resetForm(): void {
  formData.value = { name: props.initialName, token: '' };
}

defineExpose({ resetForm, handleSubmit });
</script>

<template>
  <div>
    <n-alert type="info" style="margin-bottom: 16px">
      <strong>Important:</strong> All users in your organization can see the scope and details of
      this GAIA token{{
        isEdit
          ? ''
          : ". Make sure to follow your organization's security policies when creating or managing GAIA tokens."
      }}
    </n-alert>

    <n-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-placement="left"
      label-width="auto"
      require-mark-placement="right-hanging"
    >
      <n-form-item label="GAIA Token Name" path="name">
        <gaia-input v-model:value="formData.name" placeholder="Enter GAIA token name" />
      </n-form-item>

      <n-form-item :label="isEdit ? 'New GAIA Token Value' : 'GAIA Token Value'" path="token">
        <gaia-password-input
          v-model:value="formData.token"
          :placeholder="isEdit ? 'Enter new GAIA token value' : 'Enter GAIA token value'"
        />
      </n-form-item>
    </n-form>
  </div>
</template>
